import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { prompts, scans } from "../../api/endpoints";
import RunPromptModal from "../../components/RunPromptModal.jsx";
import { useSelectedWebsite } from "./Layout.jsx";

const CATEGORY_KEYS = [
  "awareness",
  "consideration",
  "decision",
  "branded",
  "competitor",
  "other",
];

export default function Prompts() {
  const { t } = useTranslation();
  const { websiteId } = useSelectedWebsite();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ text: "", category: "awareness" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // The prompt currently open in the Run modal. Holds the full prompt
  // object so the modal can show its text without an extra fetch.
  const [runningPrompt, setRunningPrompt] = useState(null);
  const [toast, setToast] = useState(null);
  // Phase 1: niche-aware prompt suggestions surfaced above the form.
  const [suggestions, setSuggestions] = useState(null); // null = not loaded
  const [suggestNiche, setSuggestNiche] = useState("");
  const [suggestLoading, setSuggestLoading] = useState(false);
  // Phase 4: prompts where competitors are winning ("Competitors win here").
  const [discovery, setDiscovery] = useState([]);
  // Phase 3: batch-analysis state.
  //   batch: { size, dispatchedAt, lastError? } | null
  //   queue: { pending, running, active }
  const [batch, setBatch] = useState(null);
  const [batchError, setBatchError] = useState(null);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [queue, setQueue] = useState(null);
  const pollTimer = useRef(null);

  async function reload() {
    if (!websiteId) return;
    const r = await prompts.list(websiteId);
    setItems(r.data);
  }
  async function pollQueueOnce() {
    if (!websiteId) return null;
    try {
      const r = await scans.queue(websiteId);
      setQueue(r.data);
      return r.data;
    } catch {
      return null;
    }
  }
  function stopPolling() {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }
  function startPolling() {
    stopPolling();
    pollTimer.current = setInterval(async () => {
      const q = await pollQueueOnce();
      if (q && (q.active || 0) === 0) {
        stopPolling();
        // Refresh the prompt list once the batch finishes so any
        // newly-arrived data (categories, status, etc.) is reflected.
        reload();
      }
    }, 3000);
  }
  async function loadSuggestions() {
    if (!websiteId) return;
    setSuggestLoading(true);
    try {
      const r = await prompts.suggestions(websiteId);
      setSuggestions(Array.isArray(r.data?.prompts) ? r.data.prompts : []);
      setSuggestNiche(r.data?.niche || "");
    } catch {
      setSuggestions([]);
    } finally {
      setSuggestLoading(false);
    }
  }
  async function loadDiscovery() {
    if (!websiteId) return;
    try {
      const r = await prompts.discovery(websiteId);
      setDiscovery(Array.isArray(r.data) ? r.data : []);
    } catch {
      setDiscovery([]);
    }
  }
  useEffect(() => {
    reload();
    // Don't auto-call loadSuggestions(): it bills an OpenAI call every
    // time the user lands on this page. The "Suggest prompts" button
    // below triggers it on demand instead — saves the token budget
    // for actual scans.
    loadDiscovery();
    // Initial queue snapshot so an in-flight batch (started in a previous
    // session) is visible without waiting for the first poll tick.
    pollQueueOnce();
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteId]);

  if (!websiteId) {
    return <div className="text-slate-500">{t("prompts.pickWebsite")}</div>;
  }

  async function runBatch() {
    if (batchSubmitting || !items.length) return;
    setBatchSubmitting(true);
    setBatchError(null);
    try {
      const r = await prompts.runAll(websiteId);
      const data = r.data || {};
      setBatch({
        size: data.batch_size || 0,
        prompts: data.prompts || 0,
        models: data.ai_models || [],
        startedAt: Date.now(),
      });
      flashToast(t("prompts.batchDispatched", { count: data.batch_size }));
      await pollQueueOnce();
      startPolling();
    } catch (err) {
      const data = err?.response?.data;
      setBatchError(
        data?.detail ||
          (typeof data === "object" && data && (data.ai_models || data.balance)
            ? JSON.stringify(data)
            : null) ||
          t("prompts.batchFailed"),
      );
    } finally {
      setBatchSubmitting(false);
    }
  }

  async function addSuggestion(text) {
    if (!text || busy) return;
    setBusy(true);
    setError(null);
    try {
      await prompts.create(websiteId, { text, category: "awareness" });
      setSuggestions((prev) => (prev || []).filter((p) => p.text !== text));
      await reload();
    } catch (err) {
      setError(err?.response?.data?.detail || t("websites.couldNotSave"));
    } finally {
      setBusy(false);
    }
  }

  async function onCreate(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await prompts.create(websiteId, form);
      setForm({ text: "", category: "awareness" });
      await reload();
    } catch (err) {
      setError(err?.response?.data?.detail || t("websites.couldNotSave"));
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(p) {
    await prompts.update(websiteId, p.id, { is_active: !p.is_active });
    await reload();
  }

  async function remove(p) {
    if (!confirm(t("prompts.deleteConfirm"))) return;
    await prompts.remove(websiteId, p.id);
    await reload();
  }

  function flashToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }

  return (
    <div className="space-y-8 max-w-4xl relative">
      <div className="flex flex-wrap items-start gap-3 justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{t("prompts.title")}</h1>
          <p className="text-sm text-slate-600">{t("prompts.subtitle")}</p>
        </div>
        <BatchAnalysisButton
          disabled={items.length === 0 || batchSubmitting || (queue?.active ?? 0) > 0}
          submitting={batchSubmitting}
          onClick={runBatch}
          t={t}
          promptCount={items.filter((p) => p.is_active).length}
        />
      </div>

      <BatchProgressBar batch={batch} queue={queue} error={batchError} t={t} />

      {discovery.length > 0 && (
        <DiscoveryBlock rows={discovery} t={t} />
      )}

      {/* Suggestions panel: shown in three states.
            1. ``suggestions === null`` — not yet requested. Renders a
               compact "Generate suggestions" CTA. We don't call the
               OpenAI-backed endpoint until the user opts in, so the
               page costs nothing to land on.
            2. ``suggestLoading`` — request in flight.
            3. ``suggestions.length > 0`` — full list with Refresh + Add. */}
      {suggestions === null && !suggestLoading ? (
        <section className="card space-y-2 bg-brand-50/40 border-brand-200/70">
          <h2 className="text-sm font-semibold text-slate-900">
            {t("prompts.suggestionsTitle")}
          </h2>
          <p className="text-xs text-slate-600">
            {t("prompts.suggestionsCallToAction")}
          </p>
          <button
            type="button"
            onClick={loadSuggestions}
            className="btn-primary text-sm"
          >
            {t("prompts.suggestionsGenerate")}
          </button>
        </section>
      ) : (suggestLoading || (suggestions && suggestions.length > 0)) && (
        <section className="card space-y-3 bg-brand-50/40 border-brand-200/70">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">
                {t("prompts.suggestionsTitle")}
              </h2>
              <p className="text-xs text-slate-600 mt-0.5">
                {suggestNiche
                  ? t("prompts.suggestionsSubtitleNiche", { niche: suggestNiche })
                  : t("prompts.suggestionsSubtitle")}
              </p>
            </div>
            <button
              type="button"
              onClick={loadSuggestions}
              disabled={suggestLoading}
              className="text-xs text-brand-700 hover:underline whitespace-nowrap"
            >
              {suggestLoading ? t("common.loading") : t("prompts.suggestionsRefresh")}
            </button>
          </div>
          {suggestLoading ? (
            <div className="text-sm text-slate-500">{t("common.loading")}</div>
          ) : (
            <ul className="space-y-2">
              {(suggestions || []).map((s) => (
                <li
                  key={s.text}
                  className="flex items-center justify-between gap-3 rounded-md bg-white ring-1 ring-slate-200 px-3 py-2"
                >
                  <span className="text-sm text-slate-700 flex-1">{s.text}</span>
                  <VolumeBadge volume={s.volume} t={t} />
                  <button
                    type="button"
                    onClick={() => addSuggestion(s.text)}
                    disabled={busy}
                    className="text-xs text-brand-700 hover:underline whitespace-nowrap"
                  >
                    {t("prompts.suggestionsAdd")}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <form onSubmit={onCreate} className="card space-y-3">
        <textarea
          className="input"
          rows={3}
          placeholder={t("prompts.placeholder")}
          value={form.text}
          onChange={(e) => setForm({ ...form, text: e.target.value })}
          required
        />
        <div className="flex items-center gap-3">
          <select
            className="block rounded-md border-slate-300 text-sm focus:border-brand-500 focus:ring-brand-500"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          >
            {CATEGORY_KEYS.map((c) => (
              <option key={c} value={c}>
                {t(`prompts.categories.${c}`)}
              </option>
            ))}
          </select>
          <button className="btn-primary ml-auto" disabled={busy}>
            {busy ? t("prompts.adding") : t("prompts.add")}
          </button>
        </div>
        {error && <div className="text-sm text-red-600">{error}</div>}
      </form>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("prompts.colPrompt")}</th>
              <th className="px-4 py-3 font-medium">{t("prompts.colStage")}</th>
              <th className="px-4 py-3 font-medium">{t("prompts.colStatus")}</th>
              <th className="px-4 py-3 font-medium w-px"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {items.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-4 py-6 text-slate-500">
                  {t("prompts.noneYet")}
                </td>
              </tr>
            ) : (
              items.map((p) => (
                <tr key={p.id}>
                  <td className="px-4 py-3 max-w-md">{p.text}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {t(`prompts.categories.${p.category}`, p.category)}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleActive(p)}
                      className={[
                        "text-xs rounded-full px-2 py-1 ring-1",
                        p.is_active
                          ? "bg-emerald-100 text-emerald-800 ring-emerald-200"
                          : "bg-slate-100 text-slate-600 ring-slate-200",
                      ].join(" ")}
                    >
                      {p.is_active ? t("prompts.active") : t("prompts.paused")}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => setRunningPrompt(p)}
                      className="text-sm text-brand-700 hover:underline mr-3"
                    >
                      {t("prompts.runNow")}
                    </button>
                    <button
                      onClick={() => remove(p)}
                      className="text-sm text-red-600 hover:underline"
                    >
                      {t("prompts.delete")}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {runningPrompt && (
        <RunPromptModal
          websiteId={websiteId}
          prompt={runningPrompt}
          onClose={() => setRunningPrompt(null)}
          onDispatched={() => flashToast(t("prompts.scanQueued"))}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 rounded-lg bg-slate-900 text-white px-4 py-3 text-sm shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

/* ─── Suggested-prompt popularity: real AI-search-volume badge ──── */
/* Was a 1-5 star rating of the LLM's own guess; now DataForSEO's real
   ai_search_volume for the exact phrase (see visibility/metadata.py::
   suggest_prompts), so it renders as a real number — 0 is a legitimate
   "no data for this exact phrase" result, not an error. */

function VolumeBadge({ volume, t }) {
  const n = Number(volume) || 0;
  return (
    <span
      className="shrink-0 inline-flex items-center rounded-full bg-slate-100 text-slate-700 ring-1 ring-slate-200 text-[11px] px-2 py-0.5 mono"
      title={t("prompts.suggestionsVolumeLabel", { volume: n })}
    >
      {n.toLocaleString()}
    </span>
  );
}

/* ─── Phase 4: 'Competitors win here' block ────────────────────── */

function DiscoveryBlock({ rows, t }) {
  return (
    <section className="card border-amber-200 bg-amber-50/70 space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-amber-900">
          {t("prompts.discoveryTitle")}
        </h2>
        <p className="text-xs text-amber-800/80 mt-0.5">
          {t("prompts.discoverySubtitle")}
        </p>
      </div>
      <ul className="space-y-2">
        {rows.map((row) => (
          <li
            key={row.prompt_id}
            className="rounded-md bg-white ring-1 ring-amber-100 px-3 py-2 text-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-slate-900 truncate">{row.prompt_text}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {t("prompts.discoveryMissRate", {
                    rate: row.brand_miss_rate,
                    scans: row.scans,
                  })}
                </div>
              </div>
              <span className="inline-flex shrink-0 items-center rounded-full bg-amber-100 text-amber-900 ring-1 ring-amber-200 text-[11px] px-2 py-0.5">
                {t("prompts.discoveryBadge")}
              </span>
            </div>
            {row.competitors.length > 0 && (
              <div className="mt-1.5 text-xs text-slate-700 flex flex-wrap gap-1.5">
                {row.competitors.map((c) => (
                  <span
                    key={c.label}
                    className="inline-flex items-center gap-1 rounded-full bg-slate-100 ring-1 ring-slate-200 px-2 py-0.5"
                  >
                    <span className="font-medium">{c.label}</span>
                    <span className="text-slate-500">×{c.mentions}</span>
                  </span>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ─── Phase 3: batch analysis UI ───────────────────────────────── */

function BatchAnalysisButton({ disabled, submitting, onClick, t, promptCount }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      title={
        promptCount === 0
          ? t("prompts.batchNoPrompts")
          : t("prompts.batchTooltip", { count: promptCount })
      }
    >
      {submitting && (
        <svg
          className="animate-spin h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
          <path
            d="M12 2a10 10 0 0 1 10 10"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
      )}
      <span>{t("prompts.runBatch")}</span>
    </button>
  );
}

function BatchProgressBar({ batch, queue, error, t }) {
  // Show the bar while a batch is active (queue.active > 0) OR while a
  // freshly-dispatched batch hasn't been picked up yet (queue might still
  // read 0 for ~1 second after the POST returns).
  const active = queue?.active || 0;
  const showing = Boolean(batch) && (active > 0 || Date.now() - (batch?.startedAt || 0) < 4000);
  if (error) {
    return (
      <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
        {error}
      </div>
    );
  }
  if (!showing) return null;
  const size = batch?.size || 0;
  const done = Math.max(0, size - active);
  const pct = size > 0 ? Math.min(100, Math.round((done / size) * 100)) : 0;
  return (
    <div className="rounded-2xl border border-brand-200 bg-brand-50/60 p-4">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="font-medium text-brand-800">
          {t("prompts.batchProgressTitle")}
        </span>
        <span className="mono text-brand-700">
          {done}/{size} · {pct}%
        </span>
      </div>
      <div className="mt-2 h-2 w-full rounded-full bg-brand-100 overflow-hidden">
        <div
          className="h-full bg-brand-500 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-1 text-xs text-brand-700">
        {t("prompts.batchProgressSubtitle", {
          running: queue?.running || 0,
          pending: queue?.pending || 0,
        })}
      </div>
    </div>
  );
}
